"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AddStateOffcanvas } from "./add"
import { DeleteStateModal } from "./delete"
import {  EditStateOffcanvas } from "./edit"
import { StatesList } from "./list"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"

interface State {
  id: number
  state_name: string
  circle_name?: string | null
  district_count: number
  is_active: boolean
  created_by: number
  suspended_by?: number | null
  suspended_at?: string | null
  created_at: string
  updated_at: string
}

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

interface StateFormData {
  state_name: string
  circle_name?: string | null
}

interface EditStateFormData extends StateFormData {
  id: number
  is_active?: boolean
}

export const StateManagement: React.FC = () => {
  // State management
  const [states, setStates] = useState<State[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })

  // Loading states
  const [statesLoading, setStatesLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Modal/Offcanvas states
  const [showAddState, setShowAddState] = useState(false)
  const [showEditState, setShowEditState] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedState, setSelectedState] = useState<State | null>(null)

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("")
  const [activeFilter, setActiveFilter] = useState<string>("all")

  // API Functions
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "ASC" | "DESC" }>({
    field: "created_at",
    direction: "DESC",
  })

  // Debounced search — API fires only after 400 ms of inactivity
  const debouncedSearch = useDebounce(searchTerm, 400)

  const fetchStates = async (
    search = "",
    page = 1,
    isActive?: string,
    limitValue?: number,
    sort = sortConfig,
  ) => {
    setStatesLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const queryParams: any = {
        page: page,
        limit: effectiveLimit,
      }

      if (search.trim()) {
        queryParams.search = search
      }

      if (isActive && isActive !== "all") {
        queryParams.is_active = isActive === "active"
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      const response = await api.get("/state-circle/list", {
        params: queryParams,
      })

      if (response.data.success) {
        setStates(response.data.data.states || [])
        setPagination(response.data.data.pagination || pagination)
      } else {
        console.error("API returned success: false")
      }
    } catch (error) {
      console.error("Error fetching states:", error)
    } finally {
      setStatesLoading(false)
    }
  }

  const fetchStateById = async (stateId: number): Promise<State | null> => {
    try {
      const response = await api.get(`/state-circle/${stateId}`)

      if (response.data.success) {
        return response.data.data.state
      }
      return null
    } catch (error) {
      console.error("Error fetching state by ID:", error)
      return null
    }
  }

  const createState = async (stateData: StateFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/state-circle/create", stateData)

      if (response.data.success) {
        await fetchStates()
        setShowAddState(false)
        console.log("State created successfully")
      } else {
        console.error("Failed to create state:", response.data.message)
      }
    } catch (error) {
      console.error("Error creating state:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateState = async (stateData: EditStateFormData) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/state-circle/${stateData.id}`, stateData)

      if (response.data.success) {
        await fetchStates()
        setShowEditState(false)
        setSelectedState(null)
        console.log("State updated successfully")
      } else {
        console.error("Failed to update state:", response.data.message)
      }
    } catch (error) {
      console.error("Error updating state:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const deleteState = async () => {
    if (!selectedState) return

    setActionLoading(true)
    try {
      const response = await api.delete(`/state-circle/${selectedState.id}`)

      if (response.data.success) {
        await fetchStates()
        setShowDeleteModal(false)
        setSelectedState(null)
        console.log("State deleted successfully")
      } else {
        console.error("Failed to delete state:", response.data.message)
      }
    } catch (error) {
      console.error("Error deleting state:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const toggleStateStatus = async (state: State) => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/state-circle/${state.id}/toggle`, {
        is_active: !state.is_active,
      })

      if (response.data.success) {
        await fetchStates()
        console.log(`State ${!state.is_active ? "activated" : "suspended"} successfully`)
      } else {
        console.error("Failed to toggle state status:", response.data.message)
      }
    } catch (error) {
      console.error("Error toggling state status:", error)
    } finally {
      setActionLoading(false)
    }
  }

  // Event handlers
  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // Actual API call is triggered by the debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchStates(debouncedSearch, 1, activeFilter, undefined, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter)
    fetchStates(searchTerm, 1, filter, undefined, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchStates(searchTerm, page, activeFilter, undefined, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchStates(searchTerm, 1, activeFilter, limit, sortConfig)
  }

  const handleAddState = () => {
    setShowAddState(true)
  }

  const handleEditState = async (state: State) => {
    // Fetch full state details if needed
    const fullState = await fetchStateById(state.id)
    if (fullState) {
      setSelectedState(fullState)
      setShowEditState(true)
    }
  }

  const handleDeleteState = (state: State) => {
    setSelectedState(state)
    setShowDeleteModal(true)
  }

  const handleCloseModals = () => {
    setShowAddState(false)
    setShowEditState(false)
    setShowDeleteModal(false)
    setSelectedState(null)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchStates(searchTerm, pagination.page, activeFilter, undefined, sort)
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchStates()
  }, [])

  return (
    <div className="">
      <div className="">
        {/* Main States List */}
        <StatesList
          states={states}
          pagination={pagination}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          activeFilter={activeFilter}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onAddState={handleAddState}
          onEditState={handleEditState}
          onDeleteState={handleDeleteState}
          onToggleStatus={toggleStateStatus}
          onSort={handleSort}
          loading={statesLoading}
          actionLoading={actionLoading}
          searchValue={searchTerm}
          sortState={sortConfig}
        />

        {/* Add State Offcanvas */}
        <AddStateOffcanvas
          isOpen={showAddState}
          onClose={handleCloseModals}
          onSubmit={createState}
          loading={actionLoading}
        />

        {/* Edit State Offcanvas */}
        <EditStateOffcanvas
          isOpen={showEditState}
          onClose={handleCloseModals}
          onSubmit={updateState}
          state={selectedState}
          loading={actionLoading}
        />

        {/* Delete State Modal */}
        <DeleteStateModal
          isOpen={showDeleteModal}
          onClose={handleCloseModals}
          onDelete={deleteState}
          text={
            selectedState
              ? `Are you sure you want to delete the state "${selectedState.state_name}"${
                  selectedState.circle_name ? ` (Circle: ${selectedState.circle_name})` : ""
                }? This action cannot be undone.`
              : ""
          }
          loading={actionLoading}
        />
      </div>
    </div>
  )
}
